import axios from "axios";
import React, {Component} from "react";
import Dexie from "dexie";

const SIZE_LIMIT_BYTES = 1024 * 1024 * 1024 // 1 GB
const STALE_UNUPLOADED_TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days
const CLEANUP_BATCH_SIZE = 20
const CLEANUP_MAX_ITERATIONS = 500
const VIDEO_CHUNK_SIZE = 5 * 1024 * 1024 // 5 MB per chunk, resumable upload


class PhotoStorage extends Component {
    constructor(props) {
        super(props)
        this.dbName = "PhotoUploadDB"
        this.storeName = "photos"
        this.db = new Dexie(this.dbName)
        this.db.version(4).stores({
            [this.storeName]: "++id, &filename, uri, destination, uploaded, &createdAt, uploadedAt, serverUrl, bookedTime, uploadProgress"
        })
        
        // Флаг для предотвращения одновременной загрузки
        this.isUploading = false
    }
    async componentDidMount() {
        if(this.interval){
            clearInterval(this.interval)
        }
        window.addEventListener("message", this.receiveMessage, false)
        await this.cleanupOldPhotos()
        this.startUploadQueue()
    }

    componentWillUnmount() {
        clearInterval(this.interval)
        window.removeEventListener("message", this.receiveMessage, false)
    }

    receiveMessage = async (event) => {
        if (event.data.type === "addPhotoToQueue") {
            await this.savePhoto(event.data.uri, event.data.filename, event.data.destination ?? 'monitoring')
        }
        if (event.data.type === "addVideoToQueue") {
            await this.saveVideo(event.data.blob, event.data.filename, event.data.destination ?? 'brands')
        }
    }

    async savePhoto(photoUri, filename, destination) {
        if (!photoUri) {
            console.warn(`Пропускаем постановку в очередь ${filename}: пустые данные изображения`)
            return
        }
        await this.db[this.storeName].add({
            uri: photoUri,
            filename,
            destination,
            uploaded: false,
            createdAt: Date.now(),
            uploadedAt: null,
            bookedTime: null,
            uploadProgress: 0
        })
    }

    async saveVideo(blob, filename, destination) {
        try {
            await this.db[this.storeName].add({
                uri: null,
                blob,
                kind: 'video',
                totalSize: blob.size,
                filename,
                destination,
                uploaded: false,
                createdAt: Date.now(),
                uploadedAt: null,
                bookedTime: null,
                uploadProgress: 0
            })
        } catch (e) {
            console.error('Ошибка сохранения видео в очередь', e)
            window.postMessage({type: 'error', text: 'Не удалось сохранить видео в очередь. Возможно, хранилище переполнено — дождитесь загрузки текущих файлов.'})
        }
    }

    startUploadQueue() {
        this.interval = setInterval(async () => {
            await this.uploadPendingPhotos()
            await this.cleanupOldPhotos()
        }, 10000)
    }

    async uploadPendingPhotos() {
        // Проверяем, не идет ли уже процесс загрузки
        if (this.isUploading) {
            console.log('Процесс загрузки уже запущен, пропускаем...')
            return
        }

        try {
            this.isUploading = true;
            
            const tenMinutesAgo = Date.now() - 10 * 60 * 1000; // 10 минут назад
            const photos = await this.db.photos.filter(el => 
                el.uploaded == false && 
                (el.bookedTime == null || el.bookedTime < tenMinutesAgo)
            ).limit(1).toArray(); // Берем только ОДИН файл за раз
            
            if (photos.length > 0) {
                console.log(`Загружаем файл: ${photos[0].filename}`)
                let uploaded = false
                if (photos[0].kind === 'video') {
                    uploaded = await this.uploadVideo(photos[0])
                } else {
                    uploaded = await this.uploadPhoto(photos[0])
                }

                // Fast-chain only after success: on failure the 10s interval retries,
                // so a repeatedly failing record does not hammer the server
                if (uploaded) {
                    setTimeout( () => this.uploadPendingPhotos(), 500)
                }
            }
        } catch (e) {
            console.log('Ошибка получения списка не загруженных сообщений', e)
        } finally {
            // Обязательно сбрасываем флаг в любом случае
            this.isUploading = false
        }
    }



    async uploadPhoto(photo) {
        // Records saved before the empty-uri guard, or with lost payload, can never upload - drop them
        if (!photo.uri) {
            console.warn(`Удаляем из очереди ${photo.filename}: пустые данные изображения`)
            await this.db.photos.delete(photo.id)
            return true
        }

        try {
            // Бронируем файл перед началом загрузки
            await this.db.photos.update(photo.id, {
                bookedTime: Date.now(),
                uploadProgress: 0
            })

            console.log(`Начинаем загрузку файла: ${photo.filename}`)

            const formData = new FormData()
            formData.append("image", photo.uri)
            formData.append("filename", photo.filename)

            const response = await axios.post(
                `${this.props.baseUrl}?app=fileupload2&destination=${photo.destination ?? 'monitoring'}`,
                formData,
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: async (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        await this.db.photos.update(photo.id, {
                            uploadProgress: progress
                        });
                        console.log(`Прогресс загрузки ${photo.filename}: ${progress}%`)
                    }
                }
            );

            const data = response.data;
            if (data.files && data.files[0]) {
                if (data.files[0].error) {
                    const err = String(data.files[0].error)
                    console.warn(`Сервер отклонил файл ${photo.filename}: ${err}`)
                    // Poisoned / empty payload cannot be retried. Processing errors
                    // (missing Imagick, GD, disk) should stay in the queue.
                    if (/not an image|не изображение|пуст/i.test(err)) {
                        await this.db.photos.delete(photo.id)
                        return true
                    }
                    window.postMessage({type: 'error', text: err, timeout: 4000})
                    await this.db.photos.update(photo.id, {
                        bookedTime: null,
                        uploadProgress: 0
                    })
                    return false
                }
                console.log(`Файл успешно загружен: ${photo.filename}`)
                await this.db.photos.delete(photo.id)
                window.postMessage({ type: "imageUploadedFromQueue" })
                return true
            }
        } catch (error) {
            console.error(`Ошибка загрузки файла ${photo.filename}, попробуем снова...`, error)
            // В случае ошибки сбрасываем бронирование, чтобы файл мог быть загружен повторно
            await this.db.photos.update(photo.id, {
                bookedTime: null,
                uploadProgress: 0
            })
        }
        return false
    }

    async uploadVideo(item) {
        try {
            await this.db.photos.update(item.id, {bookedTime: Date.now()})

            const base = `${this.props.baseUrl}?app=fileupload2`
            const query = `destination=${item.destination ?? 'brands'}&filename=${item.filename}`

            // Server is the source of truth: always resync the confirmed offset first
            const statusResp = await axios.get(`${base}&act=videoStatus&${query}`, {withCredentials: true})

            if (statusResp.data.complete) {
                await this.db.photos.delete(item.id)
                window.postMessage({type: "imageUploadedFromQueue"})
                return true
            }

            let offset = statusResp.data.size ?? 0
            const total = item.totalSize ?? item.blob.size

            while (offset < total) {
                const chunk = item.blob.slice(offset, offset + VIDEO_CHUNK_SIZE)

                const formData = new FormData()
                formData.append("chunk", chunk, item.filename)
                formData.append("filename", item.filename)
                formData.append("offset", offset)
                formData.append("total_size", total)

                const resp = await axios.post(`${base}&act=videoChunk&destination=${item.destination ?? 'brands'}`, formData, {
                    withCredentials: true,
                    headers: {'Content-Type': 'multipart/form-data'}
                })

                if (resp.data.error) {
                    throw new Error(resp.data.error)
                }

                // On offset mismatch the server returns its actual size - resume from there
                offset = resp.data.size ?? (offset + chunk.size)

                // Refresh booking each chunk: a 200 MB upload on slow network can exceed the 10 min booking window
                await this.db.photos.update(item.id, {
                    bookedTime: Date.now(),
                    uploadProgress: Math.min(100, Math.round((offset * 100) / total))
                })

                if (resp.data.complete) {
                    break
                }
            }

            await this.db.photos.delete(item.id)
            console.log(`Видео успешно загружено: ${item.filename}`)
            window.postMessage({type: "imageUploadedFromQueue"})
            return true
        } catch (error) {
            console.error(`Ошибка загрузки видео ${item.filename}, докачаем позже...`, error)
            // Do NOT reset uploadProgress: the received bytes stay on the server, upload will resume
            await this.db.photos.update(item.id, {bookedTime: null})
        }
        return false
    }

    async cleanupOldPhotos() {
        try {
            await this.db.photos
                .filter((photo) => photo.uploaded === true)
                .delete()

            const staleCutoff = Date.now() - STALE_UNUPLOADED_TTL_MS
            await this.db.photos
                .filter((photo) => photo.uploaded !== true && photo.createdAt < staleCutoff)
                .delete()

            await this.enforceSizeLimit()
        } catch (error) {
            console.warn("Ошибка очистки PhotoUploadDB", error)
        }
    }

    async enforceSizeLimit() {
        if (!navigator?.storage?.estimate) {
            return
        }

        let estimate = await navigator.storage.estimate()
        if (!estimate.usage || estimate.usage < SIZE_LIMIT_BYTES) {
            return
        }

        let lastUsage = estimate.usage
        for (let i = 0; i < CLEANUP_MAX_ITERATIONS; i++) {
            const oldestPhotoIds = await this.db.photos
                .orderBy("createdAt")
                .limit(CLEANUP_BATCH_SIZE)
                .primaryKeys()

            if (oldestPhotoIds.length === 0) {
                break
            }

            await this.db.photos.bulkDelete(oldestPhotoIds)

            estimate = await navigator.storage.estimate()
            if (!estimate.usage || estimate.usage < SIZE_LIMIT_BYTES) {
                break
            }

            if (estimate.usage >= lastUsage) {
                break
            }

            lastUsage = estimate.usage
        }
    }

    render() {
        return null
    }
}

export default PhotoStorage
